import * as React from 'react';
import type { Location as BaseLocation } from '@bondvet/types/locations';
import type { Coordinates } from '@bondvet/types';
import type {
    FileNode as GatsbyImageFileNode,
    IGatsbyImageDataParent,
} from 'gatsby-plugin-image/dist/src/components/hooks';
import type { City } from '@bondvet/types/cities';
import type { LocationPageData } from 'data/pageDefaults/location';
import { DayOfTheWeek } from '@bondvet/types/common';
import type { IGatsbyImageData } from 'gatsby-plugin-image';
import { ClinicTeamMember } from '@bondvet/types/clinicTeam';
import {
    GreenhouseDepartmentNode as BaseDepartmentNode,
    GreenhouseJobNode as BaseJobNode,
    GreenhouseLocationNode as BaseLocationNode,
} from '@bondvet/gatsby-source-greenhouse/types';

export type { City };

export interface FileObject {
    alt: string;
    fileName: string;
    mime: string;
    path: string;
    fileId: string;
}

export interface EmployeeObject {
    employeeId?: string;
    department: Department | null;
    image?: FileObject;
    imageId?: string;
    name: string;
    role: string;
    sortOrder?: number;
    leadershipTeamSortOrder?: number;
    medicalTeamSortOrder?: number;
    leadershipTeam: boolean;
    description: string;
    longDescription: string;
    paragraph1: string;
    paragraph2: string;
    quote: string;
}

export enum Department {
    hq = 'hq',
    doctors = 'doctors',
}

export const GRAPHQL_CLIENT_NAMES = {
    default: 'bond',
    auth: 'bondAuth',
    booking: 'bondBooking',
    promoCodes: 'bondPromoCodes',
    creditCards: 'bondCreditCards',
    newsletter: 'bondNewsletter',
    voglio: 'voglio',
    experiments: 'bondExperiments',
} as const;

export type GraphQLClientNamesType = typeof GRAPHQL_CLIENT_NAMES;
export type GraphQLClientNameAlias = keyof GraphQLClientNamesType;

export type GraphQLClientName = GraphQLClientNamesType[GraphQLClientNameAlias];

export type FileNode =
    | GatsbyImageFileNode
    | IGatsbyImageDataParent
    | IGatsbyImageData;

export type FileNodeWithAltText = FileNode & {
    alt: string;
};

export interface SvgImage {
    publicURL: string;
}

export type FileNodeOrSvg = FileNode | SvgImage;

export interface ComingSoonCard {
    title?: string;
    content?: string;
    image?: string;
    imageObject?: ImageObject | FileObject | null;
}

export interface UploadNode {
    id: string;
    file: {
        childImageSharp?: IGatsbyImageDataParent<unknown>;
    };
}

export type Database = import('firebase/app').default.database.Database;
export type Storage = import('firebase/app').default.storage.Storage;
export type DataSnapshot = import('firebase/app').default.database.DataSnapshot;
export type Reference = import('firebase/app').default.database.Reference;

export type AuthError = import('firebase/app').default.auth.Error;

export type Observer<T> = import('firebase/app').default.Observer<T>;
export type Unsubscribe = import('firebase/app').default.Unsubscribe;

export type AuthUser = import('firebase/app').default.User;

export enum ListStyle {
    blog = 'blog',
    clinics = 'clinics',
}

export interface PageRecord<
    PageData extends Record<string, unknown> = Record<string, unknown>,
> {
    slug?: string;
    listStyle?: ListStyle;
    title?: string;
    description?: string;
    isDraft?: boolean;
    googleReviewsLocationIds?: string[];
    adbars?: Record<string, string>;
    pageData?: PageData;
}

export interface Page<
    PageData extends Record<string, unknown> = Record<string, unknown>,
> extends PageRecord<PageData> {
    slug: string;
}

export interface CardData {
    title: string;
    text: string;
}

export interface MediaLinkData {
    imageId?: string;
    url: string;
    title: string;
    image?: FileNode;
    defaultImage?: string;
}

export interface FaqItem {
    id: number;
    title: string;
    text: string;
}

export interface FaqSection {
    title: string;
    faqItems: FaqItem[];
}

export interface TextImage {
    title: string;
    subTitle?: string;
    content: string;
    buttonText?: string;
}

export interface TextBlockWithCTA {
    title: string;
    content: string;
    cta?: CtaButtonData;
    hasSeparator?: boolean;
    usePeachBackground?: boolean;
}

export interface Perk {
    id: string;
    icon: React.ReactNode;
    label: React.ReactNode;
}

export interface HeroCarouselItemData {
    id: string;
    title: string;
    subtitle?: string;
    content: string;
    image: string;
    altText: string;
    mainCta: CtaButtonData;
    secondaryCta: CtaButtonData;
    scheduleEnabled?: boolean;
    enableDate?: string;
    scheduleDisabled?: boolean;
    disableDate?: string;
}

export interface HomePageHeroData {
    title: string;
    clinicBookingText: string;
    telehealthBookingText: string;
}

export interface HeroCarouselData {
    autoScroll: boolean;
    scrollInterval: number;
    heroes: HeroCarouselItemData[];
}

export interface DifferentData {
    title: string;
    text: string;
    cards: ReadonlyArray<CardData>;
}

export interface MediaLinksData {
    title: string;
    links: ReadonlyArray<MediaLinkData>;
}

export interface TestimonialData {
    quote: string;
    name: string;
}

export interface TextImageCenteredTitleData {
    title: string;
    subtitle: string;
    content: string;
    buttonText: string;
}

export interface LocationsMapData {
    title: string;
    subtitle: string;
}

export interface CtaFooterData {
    title: string;
}

export interface VideoData {
    video: string;
}

export interface ActionableVideoData extends VideoData {
    title: string;
    action: string;
}

interface WistiaVideoImage {
    width: number;
    height: number;
}

export interface WistiaVideoData {
    id: string;
    stillImage: WistiaVideoImage;
}

export interface PageVideoData {
    slug: string;
    video: WistiaVideoData;
}

export type PageVideos = readonly PageVideoData[];

export interface PageImage extends ResponsiveImage {
    file?: FileNode;
}

export interface PageImageData {
    slug: string;
    alt?: string;
    image: PageImage;
}

export type PageImages = readonly PageImageData[];

export interface WhatWeDoData {
    title: string;
    text: string;
    buttonText: string;
    topVideo: ActionableVideoData;
    bottomVideo: ActionableVideoData;
}

export type CtaButtonData = {
    text?: string;
    action?: string;
};

export type HomePageData = {
    hero?: HomePageHeroData;
    heroCarousel?: HeroCarouselData;
    different?: DifferentData;
    whatWeDo?: WhatWeDoData;
    mediaLinks?: MediaLinksData;
    testimonial?: TestimonialData;
    textImage?: TextImageCenteredTitleData;
    locations?: LocationsMapData;
    cta?: CtaFooterData;
};

export type HomePage = Page<HomePageData>;

export type PageDataImageImage<File = ResponsiveImage> = {
    alt?: string;
    file: File;
};

export type PageDataImage<File = ResponsiveImage> = {
    slug: string;
    alt: string;
    image: PageDataImageImage<File>;
};

export type PageDataImages<File = ResponsiveImage> =
    readonly PageDataImage<File>[];

export type TelehealthPageData = {
    hero?: {
        title: string;
        subTitle: string;
        cta?: CtaButtonData;
        cta2?: CtaButtonData;
    };
    benefits?: {
        title: string;
        items: string[];
    };
    process?: {
        items: {
            title: string;
            content: string;
        }[];
        cta?: CtaButtonData;
    };
    symptoms: {
        title: string;
        items: {
            id: string;
            sortOrder: number;
            label: string;
            to: string;
            external: boolean;
        }[];
        note: string;
    };
    textImageCenteredTitle: {
        title: string;
        content: string;
        cta?: CtaButtonData;
    };
    faq?: FaqSection;
    cta: {
        title: string;
        content: string;
        ctaButton?: CtaButtonData;
    };
};

export type VeterinaryClinicServicesPageData = {
    tinyHero?: {
        title: string;
        text: string;
    };
    faq?: FaqSection;
    testimonial?: {
        quote: string;
        name: string;
    };
    cta?: {
        title: string;
    };
};

export type AboutUsPageData = {
    aboutHero?: {
        title: string;
        text: string;
        bookingButtonText: string;
        video?: string;
    };
    medicalTeam?: {
        title: string;
        showTeamBios: boolean;
        teamMembers?: ReadonlyArray<{
            employeeId: string;
            sortOrder: number;
        }>;
    };
    regionalLeadershipTeam?: {
        title: string;
        showTeamBios: boolean;
        teamMembers?: ReadonlyArray<{
            employeeId: string;
            sortOrder: number;
        }>;
    };
    leadershipTeam?: {
        title: string;
        showTeamBios: boolean;
        teamMembers?: ReadonlyArray<{
            employeeId: string;
            sortOrder: number;
        }>;
    };
    testimonial?: {
        quote: string;
        name: string;
    };
    values?: {
        title: string;
        text: string;
    };
    arrowTabs?: {
        id: string;
        label: string;
        content: string;
    }[];
    cta?: {
        title: string;
    };
};

export type LaunchingSoonPageData = {
    hero: {
        title: string;
        subtitle: string;
    };
    newsletterSignUp: {
        title: string;
    };
    textCenteredTitle: TextImage & {
        video: string;
        videoText: string;
    };
    cities: Record<string, string>;
};

export type UrgentCarePageData = {
    tinyHero?: {
        boxTagText: string;
        title: string;
        buttonText: string;
    };
    beigeBox?: {
        title: string;
        content1: string;
        content2: string;
    };
    careExplainer?: {
        title: string;
        description: string;
        buttonText: string;
        cardContent: string;
    };
    logoText?: {
        content: string;
    };
};

export type PreventiveCarePageData = {
    tinyHero?: {
        boxTagText: string;
        title: string;
        buttonText: string;
    };
    beigeBox?: {
        title: string;
        content: string;
    };
    careExplainer?: {
        title: string;
        description: string;
        buttonText: string;
        cardContent: string;
    };
    logoText?: {
        content: string;
    };
};

export type PuppyVisitsPageData = {
    slug?: string;
    tinyHero?: {
        title: string;
        subtitle: string;
        content: string;
        buttonText: string;
    };
    beigeBox?: {
        title: string;
        content: string;
    };
    beigeBox2?: {
        title: string;
        content: string;
    };
    vaccines?: {
        disclaimer: string;
        elements: {
            label: string;
            title: string;
            vaccineCategories: {
                label: string;
                vaccines: string[];
            }[];
        }[];
    };
    vaccineExplanation?: {
        id: string;
        label: string;
        description: string;
    }[];
    beigeBox3?: {
        title: string;
        content: string;
        buttonText: string;
    };
    cta: {
        title: string;
    };
};

export type PetTravelPageData = {
    hero?: {
        title: string;
        subTitle: string;
        cta: CtaButtonData;
    };
    benefits?: {
        title: string;
        items: string[];
    };
    process?: {
        items: {
            title: string;
            content: string;
        }[];
        cta?: CtaButtonData;
    };
    textImageCenteredTitle?: {
        title: string;
        content: string;
        alt?: string;
    };
    faq?: FaqSection;
};

export type GenericPageData = {
    title: string;
    content: string;
};

export type GenericSurgeryPageData = {
    showTextImageLeftTitle2: boolean;
    // this is currently set directly on the RTDB
    appointmentReasonId?: string;
};

export type SurgeryPageData = GenericSurgeryPageData & {
    hero?: {
        title: string;
        subTitle: string;
        cardText: string;
        content: string;
        contentCtaText: string;
        cardCtaText: string;
        mainCtaText?: string;
    };
    textImageLeftTitle1?: {
        title: string;
        content: string;
        video: string;
        videoText?: string;
    };
    services?: {
        title: string;
        items?: {
            label: string;
            to?: string;
        }[];
    };
    textImageLeftTitle2?: {
        title: string;
        content: string;
        buttonText: string;
        person1: { name: string };
        person2: { name: string };
    };
    faq?: FaqSection;
};

export type OrthopedicSurgeryPageData = GenericSurgeryPageData & {
    hero?: {
        title: string;
        subTitle: string;
        cardText: string;
        content: string;
        cta?: CtaButtonData;
    };
    darkBox?: {
        title: string;
        content: string;
    };
    lightBox?: {
        title: string;
        content: string;
        video: string;
        videoText: string;
    };
    tabbedBox?: {
        items: {
            id: string;
            label: string;
            content: string;
        }[];
    };
    peopleBox: {
        title: string;
        content: string;
        buttonText: string;
        person1: { name: string };
        person2: { name: string };
    };
};

export type DentalCarePageData = {
    hero?: {
        title: string;
        subTitle: string;
        cardText: string;
        content: string;
        cardCtaText: string;
        mainCtaText?: string;
    };
    appointmentReasonId?: string;
    textSmallImageCenteredTitle?: {
        title: string;
        content: string;
        video: string;
        videoText: string;
        videoLinkText: string;
    };
    textImageLeftTitle?: {
        title: string;
        content: string;
        buttonText: string;
        video: string;
        videoText: string;
        videoLinkText: string;
    };
    faq?: FaqSection;
};

export type ReferralsPageData = {
    hero: {
        title: string;
        text: string;
    };
    referralForm: {
        title: string;
        content: string;
        cta: CtaButtonData;
        terms: string;
    };
};

export type UserInput = {
    email: string;
    admin: boolean;
};

export type User = {
    uid: string;
    email: string;
    emailVerified: boolean;
    customClaims?: { admin: boolean };
};

export type LinkData = {
    id: string;
    to: string;
    external: boolean;
    label: string;
    boldLabel?: string;
    sortOrder: number;
    subItems?: ReadonlyArray<LinkData>;
};

export type StoredLinkData = Omit<LinkData, 'id' | 'subItems'> & {
    subItems?: Record<string, StoredLinkData>;
};

// Computed for App usage
export type MenuItem = LinkData;

export type MenuData = {
    items: ReadonlyArray<MenuItem>;
};

export interface NearbyLocation {
    distance: number;
    locationId: string;
}

export type Location = Pick<
    BaseLocation,
    | '_id'
    | 'coordinates'
    | 'name'
    | 'timezone'
    | 'fullAddress'
    | 'phoneNumber'
    | 'intlPhoneNumber'
> & {
    slug: string | null;
    shortSlug: string | null;
    description: string | null;
    googlePhoneConversionId: string | null;
    displayAddress?: string | null;
    bookingSettings?: {
        requiresCreditCard?: boolean;
        hasFirstVisitPromo?: boolean;
        firstVisitExamPromoHtml?: string;
        requirePetSignalmentForNewPets?: boolean;
        allowIndividualProviderBookings?: boolean;
    };
    paragraphAsideImage?: string;
    showTeamBios?: boolean;
    dropOffsOnly?: boolean;
    bookingEnabled?: boolean;
    surgeryCalendlyUrl?: string;
    openDate?: string;
    sortOrder?: number;
    mapInfo?: LocationMapInfo;
    cardText?: {
        booking: string;
        service: string;
        vet: string;
    };
    texts?: LocationTexts;
    tourSrc?: string | null;
    googleReviews?: GoogleReviews;
    googleReviewsLocations: ReadonlyArray<Location>;
    googleLocation?: {
        metadata?: {
            mapsUrl?: string;
            newReviewUrl?: string;
            placeId?: string;
        };
    };
    nearestLocations: ReadonlyArray<NearbyLocation>;
    city?: Pick<City, 'id' | 'label' | 'slug'> & {
        cityId: string;
    };
    wellnessPromoDays?: ReadonlyArray<string>;
};

export type GoogleReviews = {
    averageRating: number | string;
    bestRating: number;
    reviews: Review[];
    totalReviewCount: number;
    worstRating: number;
};

export type Review = {
    rating: number;
    reviewer: Reviewer;
    comment: string;
};

export type Reviewer = {
    displayName: string;
    profilePhotoUrl: string;
};

export type LocationMapInfo = {
    intro?: string;
    subwayHeader?: string;
    subwayStations?: ReadonlyArray<SubwayStation>;
};

export type SubwayStation = {
    direction: string;
    station: string;
};

export type LocationTexts = {
    virtualTourTitle?: string;
    virtualTour?: string;
    textImageLeftTitle?: string;
    textImageLeftText?: string;
    comingSoonImageLeftTitle?: string;
    comingSoonImageLeftText?: string;
};

export type LocationWithCoordinates = Omit<Location, 'coordinates'> & {
    coordinates: Coordinates;
};

export type Image = {
    _id: string;
    url: string;
};

export type LocationImage = {
    main: boolean;
    image: string;
    imageAlt?: string;
};

export type Employee = {
    imageId?: string | null;
    employeeId?: string | null;
    name: string;
    role?: string | null;
    department?: string | null;
    description?: string | null;
    longDescription?: string | null;
    locationIds?: ReadonlyArray<string> | null;
    sortOrder?: number;
};

export type MetaImage = {
    alt?: string;
    id: number | string;
    mime?: string;
    path?: string;
    url: string;
};

export interface Promotion {
    description: string;
    validUntil: string;
}

export type CityNode = Pick<
    City,
    'label' | 'slug' | 'sortOrder' | 'title' | 'description' | 'locationOrder'
> & {
    cityId: string;
};

export type CityWithNumberOfLocations = CityNode & {
    numberOfLocations: number;
};

export type NearestLocation = {
    locationId: string;
    distance: number;
};

export type LocationWithImages = LocationWithCoordinates & {
    images?: ReadonlyArray<LocationImage>;
    mainImage?: string | null;
    teamMembers?: ReadonlyArray<Employee>;
    metaImages?: ReadonlyArray<string>;
    cardImage?: string | null;
    paragraphAsideImage?: string | null;
    comingSoonAsideImage?: string | null;
    googleReviewsLocationIds?: ReadonlyArray<string>;
    nearestLocations?: ReadonlyArray<NearestLocation>;
    promotion?: string | null;
    city?: City | null;
    pageData?: LocationPageData;
    schema?: string | null;
};

export type LocationWithCityId = Omit<LocationWithImages, 'city'> & {
    cityId: string | null;
};

export type EmployeeData = Employee & {
    id: string;
    image?: {
        file: FileNode;
    };
};

export type Event = {
    imageId?: string | null;
    title: string;
    startTime: string;
    endTime: string;
    location: string;
    address: string;
    region?: string | null;
    text: string;
    rsvpLink?: string | null;
    icalLink: string;
};

export type EventWithImage = Event & {
    id: string;
    image: ResponsiveImage;
};

export type Moment = import('moment-timezone').Moment;

export interface Option<Value = unknown> {
    value: Value;
    label: string;
    key?: string;
    cardBrand?: string;
}

export type ResponsiveImageData = {
    mobile: FileNode;
    desktop: FileNode;
};

export type ImageData = {
    id: string;
    mobile: FileNode;
    desktop: FileNode;
};

export type LocationWithImageData = LocationWithCoordinates & {
    mainImage?: ImageData;
    images?: ReadonlyArray<{ main: boolean; image: ImageData }>;
    teamMembers?: ReadonlyArray<EmployeeData>;
    cardImage?: Omit<ImageData, '_id'> | null;
    heroImage?: {
        slug?: string;
        alt?: string;
        image?: ImageData;
    };
    schema?: string | null;
};

export type CityWithLocations = City & {
    locations: ReadonlyArray<LocationWithImageData>;
};

export type ResponsiveImage = Pick<ImageData, 'mobile' | 'desktop'>;

export type ResponsiveImageWithAltText = ResponsiveImage & {
    alt: string;
};

export type ResponsiveImageWithURL = ResponsiveImage & {
    publicURL: string;
};

export type GalleryImage = {
    id: string;
    desktopSmall: FileNode;
    desktopBig: FileNode;
    desktop2ColBig: FileNode;
    mobileSmall: FileNode;
    mobileBig: FileNode;
};

export type GalleryQuery = {
    gallery1: GalleryImage;
    gallery2: GalleryImage;
    gallery3: GalleryImage;
    gallery4: GalleryImage;
};

export type GalleryImageData = {
    id: string;
    file: GalleryImage;
};

export type AsideItem = {
    id: string;
    item: React.ReactNode;
};

export type PageContextWithMetaData = {
    title?: string;
    description?: string;
    ogImage?: string;
};

export type OgImageData = {
    publicURL: string;
    childImageSharp?: {
        resize: {
            src: string;
            width: number;
        };
        original: {
            src: string;
            width: number;
        };
    };
};

export type OgImage = {
    file: OgImageData;
};

export type OgImageQuery = {
    ogImage?: OgImage;
};

export type ImageObject = {
    alt: string;
    previewSrc: string;
    fileId: string;
    mime: string;
    filename: string;
};

export type PopupObject = {
    startTime: string;
    endTime: string;
    title: string;
    content: string;
    buttonText: string;
    buttonAction: string;
    image: string | ImageObject | null;
};

export type AdbarObject = {
    startTime: string;
    endTime: string;
    title: string;
};

export type LocationTagSetting = {
    color: string;
    id: string;
    name: string;
};

export type LeftFormWithGoogleReviewsData = Pick<
    PageRecord,
    'googleReviewsLocationIds'
>;

export type LeftFormWithMetaData = LeftFormWithGoogleReviewsData &
    Pick<PageRecord, 'title' | 'description' | 'adbars'>;

export type OpeningHours = Record<
    DayOfTheWeek,
    {
        start: string;
        end: string;
    }[]
>;

export type CitiesPageData = {
    bio: {
        title: string;
        content: string;
    };
};

export type CityWithPageData = City & {
    pageData: CitiesPageData;
};

export interface MedicalExpert {
    id?: string;
    sortOrder?: number;
    teamMember?: ClinicTeamMember;
}

export type MedicalExpertWithTeamMember = MedicalExpert & {
    teamMember: {
        name: string;
        role: string;
        image: {
            file: {
                publicURL: string;
            };
            imageId: string;
            alt: string;
        };
    };
};

export type ExpertsPageData = {
    showPage?: boolean;
    hero?: {
        title?: string;
        subTitle?: string;
    };
};

export type ClinicTeamMemberWithImage = ClinicTeamMember & {
    clinicTeamMemberId: string;
    image: ResponsiveImage | null;
};

export type Script = {
    script: string;
    description: string;
};

export type HomePageDefaultsQuery = {
    mobileHero: FileNodeWithAltText;
    desktopHero: FileNodeWithAltText;
    testimonial: ResponsiveImage;
    mission: ResponsiveImage;
    wsj: FileNode;
    nym: FileNode;
    nyp: FileNode;
    nyt: FileNode;
    tc: FileNode;
    about: WistiaVideoData;
    surgeries: WistiaVideoData;
};

export type PreventiveCareDefaultsQuery = GalleryQuery & {
    hero: ResponsiveImage;
};

export type GiftCardPageData = {
    title?: string;
    banner?: string;
    refundSection?: {
        title?: string;
        content?: string;
        button?: CtaButtonData;
    };
    customerServiceSection?: {
        title?: string;
        content?: string;
    };
    hero?: {
        title: string;
        subtitle: string;
        cta: CtaButtonData;
    };
    textBlocks?: TextBlockWithCTA[];
    terms?: string;
};

export type CardWithIcon = {
    title: string;
    text: string;
};

type CarouselOrImage =
    | { carousel: CardWithIcon[]; image?: never }
    | { image: FileNodeWithAltText; carousel?: never };

export type HalfPageWithCarousel = {
    title: string;
    content: string;
    cta: CtaButtonData;
    smallPrint?: string;
    carouselOrImage: CarouselOrImage;
};

export type PaymentOptionsPageData = {
    hero: {
        title: string;
        subtitle: string;
        content: string;
        dropdownText: string;
    };
    firstSection: HalfPageWithCarousel;
    secondSection: HalfPageWithCarousel;
    thirdSection: HalfPageWithCarousel;
    faq: FaqSection;
};

export type MembershipRegion = {
    slug: string;
    label: string;
};

export type MembershipPlanData = {
    name: string;
    badge: string;
    title: string;
    price: string;
    period: string;
    perks: string[];
};

export type MembershipSavingsTableRow = {
    benefit: string;
    standard: string;
    withPlan: string;
    savings: string;
};

export type MembershipSavingsTableData = {
    headerStandardCost: string;
    headerWithPlan: string;
    headerYourSavings: string;
    rows: MembershipSavingsTableRow[];
    totalLabel: string;
    totalSavings: string;
    ariaLabel?: string;
};

export type MembershipPageData = {
    hero?: {
        title: string;
        content: string;
        cta?: CtaButtonData;
    };
    intro?: {
        title: string;
        items?: Array<{ id: string; title: string; description: string }>;
    };
    pricing?: {
        sectionTitle: string;
        perksTitle?: string;
        planAnnual: MembershipPlanData;
    };
    savingsTable?: MembershipSavingsTableData;
    faq?: FaqSection;
};

// recursively makes all arrays within an array readonly
// required for type matching with some VetSpire items
export type DeepReadonly<T> = {
    readonly [P in keyof T]: T[P] extends Array<infer U>
        ? ReadonlyArray<DeepReadonly<U>>
        : DeepReadonly<T[P]>;
};

// utility type that changes every string from the original type into ReactNode
// used to convert the types taken by the pages in the front-end
// into the CMS types that take TinaCMS components
export type ConvertTypeToPage<T> = {
    [K in keyof T]: T[K] extends string | undefined
        ? React.ReactNode | undefined
        : T[K];
};

// Cookiebot: https://support.cookiebot.com/hc/en-us/articles/4405978132242-Manual-cookie-blocking
export type CookieType =
    | 'necessary'
    | 'preferences'
    | 'statistics'
    | 'marketing';

export type ClinicHours = Partial<
    Record<
        DayOfTheWeek,
        {
            hours: { start: number; end: number } | null;
            isSpecialHours: boolean;
            usualHours: { start: number; end: number } | null;
        }
    >
>;

export type LocationHoursKey =
    | `${DayOfTheWeek}Ranges`
    | `${DayOfTheWeek}SpecialRanges`;

export type LocationHoursQuery = {
    location: {
        timezone: string;
        locationHours: Partial<Record<LocationHoursKey, number[][]>>;
    };
};

export type AllLocationHoursQuery = {
    locations: {
        _id: string;
        timezone: string;
        locationHours: Partial<Record<LocationHoursKey, number[][]>>;
    }[];
};

export type GreenhouseLocationNode = Pick<BaseLocationNode, 'name' | 'slug'>;

export type GreenhouseDepartmentNode = Pick<
    BaseDepartmentNode,
    'name' | 'slug' | 'departmentId'
>;
export type GreenhouseJobNode = Pick<
    BaseJobNode,
    | 'title'
    | 'id'
    | 'jobId'
    | 'absoluteUrl'
    | 'positionType'
    | 'jobTypes'
    | 'content'
> & {
    departments: ReadonlyArray<
        Pick<BaseDepartmentNode, 'departmentId' | 'name'>
    >;
    location?: Pick<GreenhouseLocationNode, 'slug' | 'name'>;
};

export interface GreenhousePositionsQuery {
    locations: {
        nodes: ReadonlyArray<GreenhouseLocationNode>;
    };
    departments: {
        nodes: ReadonlyArray<GreenhouseDepartmentNode>;
    };
    jobs: {
        nodes: ReadonlyArray<GreenhouseJobNode>;
    };
}

export type JobCategories = {
    commitment?: string;
    team: string;
    location: string;
};

export interface JobPosting {
    id: string;
    jobId: number;
    text: string;
    hostedUrl: string;
    positionType?: string | null;
    jobTypes?: string[] | null;
    categories: JobCategories;
    content: string;
}
